import React from 'react';
import { Grid, Card, Typography, Row, Col, Tooltip } from 'antd';
import { Table } from 'antd';
import { formatDate } from '../../utils/dateFormatters';

const { useBreakpoint } = Grid;
const { Text, Paragraph } = Typography;

/**
 * ProviderHeatmap - Heat map visualization of providers over time
 *
 * MÉTODO DE CÁLCULO DEL MAPA DE CALOR:
 *
 * 1. AGRUPACIÓN DE DATOS:
 *    - Se agrupa por Proveedor (NIT) y Fecha (RecordDate)
 *    - Cada celda representa el estado de un proveedor en una fecha específica
 *
 * 2. CÁLCULO DEL PORCENTAJE DE SALUD:
 *    - SKUs Saludables = Verde (60-80%) + Amarillo (30-60%)
 *    - % Salud = (SKUs Saludables / Total SKUs del proveedor) × 100
 *
 * 3. CLASIFICACIÓN POR COLORES:
 *    - Excelente (80-100%): #16a34a - Más del 80% saludable
 *    - Bueno (60-79%): #ca8a04 - Entre 60% y 79% saludable
 *    - Alerta (40-59%): #f97316 - Entre 40% y 59% saludable
 *    - Crítico (<40%): #dc2626 - Menos del 40% saludable
 *
 * 4. INTERPRETACIÓN:
 *    - Celdas verdes indican proveedores con inventario saludable
 *    - Celdas rojas indican proveedores que requieren atención urgente
 *    - Permite identificar patrones y tendencias por proveedor en el tiempo
 *
 * @param {Object} props
 * @param {Array} props.data - Array of DailyInventoryDistributionRecord
 */
function ProviderHeatmap({ data }) {
  const screens = useBreakpoint();
  const isXs = !screens.xs;
  const isSm = screens.sm;

  // Build a map of Nit -> ProjectName from data
  const providerNameMap = React.useMemo(() => {
    const map = {};
    if (!data || data.length === 0) return map;
    data.forEach(record => {
      if (record.Nit && record.ProjectName && !map[record.Nit]) {
        map[record.Nit] = record.ProjectName;
      }
    });
    return map;
  }, [data]);

  // Transform data for heatmap
  const heatmapData = React.useMemo(() => {
    if (!data || data.length === 0) return { columns: [], dataSource: [], stats: null };

    // Get unique providers and dates
    const providers = new Set();
    const dates = new Set();

    data.forEach(record => {
      if (record.Nit) {
        providers.add(record.Nit);
        dates.add(record.RecordDate);
      }
    });

    const sortedProviders = Array.from(providers).sort();
    const sortedDates = Array.from(dates).sort();

    // Calculate statistics
    let totalCells = 0;
    let excellentCells = 0;
    let goodCells = 0;
    let alertCells = 0;
    let criticalCells = 0;

    // Responsive column widths
    const providerWidth = isXs ? 140 : isSm ? 180 : 220;
    const dateWidth = isXs ? 65 : isSm ? 85 : 95;

    // Create columns
    const columns = [
      {
        title: 'Proveedor',
        dataIndex: 'provider',
        key: 'provider',
        fixed: 'left',
        width: providerWidth,
        render: (value) => {
          const providerName = providerNameMap[value] || '';
          const displayText = providerName || value;
          return (
            <Tooltip title={providerName ? `${providerName} (${value})` : value}>
              <Text
                style={{
                  fontSize: isXs ? 11 : 12,
                  fontWeight: 500,
                  color: '#374151'
                }}
                ellipsis={{ tooltip: false }}
              >
                {isXs && displayText.length > 16 ? displayText.substring(0, 16) + '...' : displayText}
              </Text>
            </Tooltip>
          );
        }
      },
      ...sortedDates.map(date => ({
        title: formatDate(date),
        dataIndex: date,
        key: date,
        width: dateWidth,
        align: 'center',
        responsive: isXs ? undefined : ['sm', 'md', 'lg', 'xl'],
        render: (value, record) => {
          if (!value) {
            return (
              <div style={{
                width: '100%',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f9fafb',
                borderRadius: '6px',
                fontSize: isXs ? 10 : 11,
                color: '#9ca3af'
              }}>
              —
            </div>
            );
          }

          const { percent, count, status } = value;
          const cellConfig = getCellConfig(percent);

          // Update stats
          totalCells++;
          if (status === 'excellent') excellentCells++;
          else if (status === 'good') goodCells++;
          else if (status === 'alert') alertCells++;
          else if (status === 'critical') criticalCells++;

          return (
            <Tooltip
              title={
                <div>
                  <div><strong>{formatDate(date)}</strong></div>
                  <div>Saludable: {Math.round(percent)}%</div>
                  <div>Total SKUs: {count}</div>
                  <div style={{ marginTop: 4, fontSize: '11px', color: cellConfig.descriptionColor }}>
                    {cellConfig.label}
                  </div>
                </div>
              }
            >
              <div
                style={{
                  width: '100%',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: cellConfig.bgColor,
                  borderRadius: '6px',
                  border: `1px solid ${cellConfig.borderColor}`,
                  fontSize: isXs ? 10 : 11,
                  fontWeight: 600,
                  color: cellConfig.textColor,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = `0 4px 12px ${cellConfig.shadowColor}40`;
                  e.currentTarget.style.zIndex = '10';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.zIndex = '1';
                }}
              >
                {Math.round(percent)}%
              </div>
            </Tooltip>
          );
        }
      }))
    ];

    // Create data source
    const dataSource = sortedProviders.map(provider => {
      const row = { key: provider, provider };

      sortedDates.forEach(date => {
        const record = data.find(
          d => d.Nit === provider && d.RecordDate === date
        );

        if (record && record.TotalReferences > 0) {
          const healthyPercent = ((record.VerdeCount + record.AmarilloCount) / record.TotalReferences) * 100;

          let status = 'critical';
          if (healthyPercent >= 80) status = 'excellent';
          else if (healthyPercent >= 60) status = 'good';
          else if (healthyPercent >= 40) status = 'alert';

          row[date] = {
            percent: healthyPercent,
            count: record.TotalReferences,
            status: status
          };
        }
      });

      return row;
    });

    const stats = {
      total: totalCells,
      excellent: excellentCells,
      good: goodCells,
      alert: alertCells,
      critical: criticalCells,
      excellentPercent: totalCells > 0 ? (excellentCells / totalCells) * 100 : 0,
      goodPercent: totalCells > 0 ? (goodCells / totalCells) * 100 : 0,
      alertPercent: totalCells > 0 ? (alertCells / totalCells) * 100 : 0,
      criticalPercent: totalCells > 0 ? (criticalCells / totalCells) * 100 : 0
    };

    return { columns, dataSource, stats };
  }, [data, isXs, isSm, providerNameMap]);

  // Get cell configuration based on health percentage
  const getCellConfig = (percent) => {
    if (percent >= 80) {
      return {
        status: 'excellent',
        label: 'Excelente',
        bgColor: '#dcfce7',
        textColor: '#166534',
        borderColor: '#86efac',
        descriptionColor: '#15803d',
        shadowColor: 'rgba(22, 163, 74,'
      };
    }
    if (percent >= 60) {
      return {
        status: 'good',
        label: 'Bueno',
        bgColor: '#fef9c3',
        textColor: '#854d0e',
        borderColor: '#fde047',
        descriptionColor: '#a16207',
        shadowColor: 'rgba(202, 138, 4,'
      };
    }
    if (percent >= 40) {
      return {
        status: 'alert',
        label: 'Alerta',
        bgColor: '#ffedd5',
        textColor: '#9a3412',
        borderColor: '#fdba74',
        descriptionColor: '#c2410c',
        shadowColor: 'rgba(234, 88, 22,'
      };
    }
    return {
      status: 'critical',
      label: 'Crítico',
      bgColor: '#fee2e2',
      textColor: '#991b1b',
      borderColor: '#fca5a5',
      descriptionColor: '#b91c1c',
      shadowColor: 'rgba(220, 38, 38,'
    };
  };

  const tableHeight = isXs ? 280 : isSm ? 340 : 400;

  if (!data || data.length === 0) {
    return (
      <Card
        bordered={false}
        className="shadow-sm"
        style={{
          height: '100%',
          borderRadius: '12px',
          backgroundColor: '#fafafa'
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300,
          color: '#9ca3af',
          textAlign: 'center',
          padding: 20
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>{'🗺️'}</div>
          <div style={{ fontSize: isXs ? 14 : 16, fontWeight: 500, marginBottom: 8 }}>
            Sin datos disponibles
          </div>
          <div style={{ fontSize: isXs ? 12 : 13 }}>
            Se requieren datos agrupados por proveedor para mostrar el mapa de calor
          </div>
        </div>
      </Card>
    );
  }

  // Helper to get legend item style
  const LegendItem = ({ color, borderColor, label, description, count, percent }) => (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '8px 12px',
      backgroundColor: color + '20',
      borderRadius: '8px',
      border: '1px solid ' + borderColor,
      flex: 1
    }}>
      <div style={{
        width: 14,
        height: 14,
        borderRadius: '4px',
        backgroundColor: color,
        border: '1px solid ' + borderColor,
        flexShrink: 0
      }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: isXs ? 11 : 12, fontWeight: 600, color: '#374151' }}>
          {label}
        </div>
        <div style={{ fontSize: isXs ? 10 : 11, color: '#6b7280' }}>
          {description}
        </div>
      </div>
      <div style={{
        fontSize: isXs ? 12 : 13,
        fontWeight: 700,
        color: borderColor
      }}>
        {count}
      </div>
    </div>
  );

  return (
    <Card
      bordered={false}
      className="shadow-sm"
      style={{
        height: '100%',
        borderRadius: '12px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
      }}
      bodyStyle={{ padding: isXs ? 16 : 20 }}
    >
      {/* Header */}
      <div style={{ marginBottom: isXs ? 16 : 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Text strong style={{ fontSize: isXs ? 14 : 16, color: '#1f2937' }}>
            {"Mapa de Calor por Proveedor"}
          </Text>
        </div>
        <Text type="secondary" style={{ fontSize: isXs ? 11 : 12 }}>
          Cada celda muestra el porcentaje de productos con buen inventario para cada proveedor y fecha. Verde = Buen estado, Rojo = Atencion urgente.
        </Text>
      </div>

      {/* Stats Summary */}
      {heatmapData.stats && (
        <div style={{
          marginBottom: isXs ? 16 : 20,
          padding: '12px 16px',
          backgroundColor: '#f9fafb',
          borderRadius: '10px',
          border: '1px solid #e5e7eb'
        }}>
          <Row gutter={[12, 12]}>
            <Col xs={12} sm={6} md={6}>
              <div style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: isXs ? 10 : 11, color: '#6b7280' }}>Proveedores con Buena Salud</Text>
                <div style={{ fontSize: isXs ? 18 : 22, fontWeight: 700, color: '#16a34a' }}>
                  {heatmapData.stats.excellent + heatmapData.stats.good}
                </div>
              </div>
            </Col>
            <Col xs={12} sm={6} md={6}>
              <div style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: isXs ? 10 : 11, color: '#6b7280' }}>Promedio Salud General</Text>
                <div style={{
                  fontSize: isXs ? 18 : 22,
                  fontWeight: 700,
                  color: '#16a34a'
                }}>
                  {Math.round(heatmapData.stats.excellentPercent + heatmapData.stats.goodPercent)}%
                </div>
              </div>
            </Col>
          </Row>
        </div>
      )}

      {/* Heatmap Table */}
      <div style={{
        marginBottom: isXs ? 16 : 20,
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: tableHeight,
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}>
        <Table
          columns={heatmapData.columns}
          dataSource={heatmapData.dataSource}
          pagination={false}
          size={isXs ? 'small' : 'middle'}
          scroll={{ x: 'max-content', y: tableHeight }}
          bordered={false}
          rowClassName={(record, index) =>
            index % 2 === 0 ? 'heatmap-row-even' : 'heatmap-row-odd'
          }
        />
        <style>{`
          .heatmap-row-even:hover td { background-color: #f9fafb !important; }
          .heatmap-row-odd:hover td { background-color: '#f3f4f6 !important; }
          .ant-table-thead > tr > th {
            background-color: #f9fafb !important;
            border-bottom: 2px solid #e5e7eb !important;
            font-weight: 600 !important;
            color: #374151 !important;
            padding: 12px 8px !important;
          }
          .ant-table-tbody > tr > td {
            padding: 4px 8px !important;
            border-bottom: 1px solid #f5f5f5 !important;
          }
        `}</style>
      </div>

      {/* Color Legend */}
      <div style={{
        marginTop: isXs ? 16 : 20,
        padding: '14px',
        backgroundColor: '#f9fafb',
        borderRadius: '10px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ fontSize: isXs ? 12 : 13, color: '#1f2937' }}>
            {"🎨 Escala de Colores"}
          </Text>
        </div>
        <Row gutter={[10, 10]}>
          <Col xs={12} sm={6}>
            <LegendItem
              color="#dcfce7"
              borderColor="#86efac"
              label="Excelente"
              description="≥80% saludable"
              count={heatmapData.stats.excellent}
              percent={heatmapData.stats.excellentPercent}
            />
          </Col>
          <Col xs={12} sm={6}>
            <LegendItem
              color="#fef9c3"
              borderColor="#fde047"
              label="Bueno"
              description="60-79% saludable"
              count={heatmapData.stats.good}
              percent={heatmapData.stats.goodPercent}
            />
          </Col>
          <Col xs={12} sm={6}>
            <LegendItem
              color="#ffedd5"
              borderColor="#fdba74"
              label="Alerta"
              description="40-59% saludable"
              count={heatmapData.stats.alert}
              percent={heatmapData.stats.alertPercent}
            />
          </Col>
          <Col xs={12} sm={6}>
            <LegendItem
              color="#fee2e2"
              borderColor="#fca5a5"
              label="Crítico"
              description="<40% saludable"
              count={heatmapData.stats.critical}
              percent={heatmapData.stats.criticalPercent}
            />
          </Col>
        </Row>
      </div>

      {/* Methodology */}
      <div style={{
        marginTop: isXs ? 16 : 20,
        padding: '14px',
        backgroundColor: '#f9fafb',
        borderRadius: '10px',
        border: '1px solid #e5e7eb'
      }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong style={{ fontSize: isXs ? 12 : 13, color: '#1f2937' }}>
            {'Como se calcula'}
          </Text>
        </div>

        <Paragraph style={{
          margin: '0 0 8px 0',
          fontSize: isXs ? 11 : 12,
          color: '#6b7280',
        }}>
          El porcentaje de salud = productos con inventario moderado o adecuado (Verde + Amarillo) dividido por el total de productos del proveedor.
        </Paragraph>

        <div style={{
          fontSize: isXs ? 10 : 11,
          color: '#6b7280',
          fontFamily: 'monospace'
        }}>
          Excelente (verde) ≥80% | Bueno (amarillo) ≥60% | Alerta (naranja) ≥40% | Critico (rojo) &lt;40%
        </div>
      </div>

      {/* Pro Tip */}
      <div style={{
        marginTop: 12,
        padding: '10px 12px',
        backgroundColor: '#eff6ff',
        borderRadius: '8px',
        border: '1px solid #bfdbfe',
        fontSize: isXs ? 10 : 11,
        color: '#1e40af',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8
      }}>
        <span style={{ fontSize: 14, flexShrink: 0 }}>💡</span>
        <Text style={{ fontSize: isXs ? 10 : 11 }}>
          <strong>Tip:</strong> Pasa el cursor sobre cada celda para ver detalles. Los colores ayudan a identificar rápidamente proveedores con problemas crónicos.
        </Text>
      </div>
    </Card>
  );
}

export default ProviderHeatmap;
